'use client';
import { InterfacePageEntityComponentType } from '@unifyapps/defs/types/page';
import { useEffect, useMemo } from 'react';
import { invariant } from 'ts-invariant';
import type { ChannelEventBus } from '../../../../emitter';
import type { WalkthroughInstanceEventBusType } from '../../../types/walkthroughInstanceEventBus';
import { createWalkthroughInstanceEventBus } from '../../../types/walkthroughInstanceEventBus';
import { useWalkthroughOrchestrator } from '../../hooks/useWalkthroughOrchestrator';

/**
 *
 * this hook is used to subscribe to the events coming from interface resource provider
 * and orchestrate the walkthroughs based on the events
 */
function useWalkthroughManagerEffects(
  walkthroughInstanceEventBus: ChannelEventBus<WalkthroughInstanceEventBusType>,
) {
  const {
    handlePageLoad,
    handlePageUnload,
    handleWalkthroughsFetched,
    moveStep,
    goToStep,
    stopWalkthrough,
    startWalkthrough,
  } = useWalkthroughOrchestrator();

  useEffect(() => {
    const unsubscribePageLoad = walkthroughInstanceEventBus.subscribe('onPageLoad', (payload) => {
      const { pageId, componentType } = payload ?? {};
      invariant(pageId, 'pageId is required');
      if (componentType === InterfacePageEntityComponentType.PAGE) {
        void handlePageLoad(pageId);
      } else {
        // not handling any other component types for now
      }
    });

    const unsubscribePageUnload = walkthroughInstanceEventBus.subscribe(
      'onPageUnload',
      (payload) => {
        const { pageId, componentType } = payload ?? {};
        invariant(pageId, 'pageId is required');
        if (componentType === InterfacePageEntityComponentType.PAGE) {
          void handlePageUnload(pageId);
        } else {
          // not handling any other component types for now
        }
      },
    );

    const unsubscribeFetchedWalkthroughs = walkthroughInstanceEventBus.subscribe(
      'onSetFetchedWalkthroughs',
      (payload) => {
        const { pageId, walkthrough } = payload ?? {};
        invariant(pageId, 'pageId is required');
        void handleWalkthroughsFetched(pageId, walkthrough);
      },
    );

    const unsubscribeControlStep = walkthroughInstanceEventBus.subscribe(
      'onControlStep',
      (payload) => {
        const { method } = payload ?? {};
        invariant(method, 'method is required');
        void moveStep(method);
      },
    );

    const unsubscribeGoToStep = walkthroughInstanceEventBus.subscribe('onGoToStep', (payload) => {
      const { stepIndex } = payload ?? {};
      invariant(typeof stepIndex === 'number', 'stepIndex is required');
      void goToStep(stepIndex);
    });

    const unsubscribeControlWalkthrough = walkthroughInstanceEventBus.subscribe(
      'onControlWalkthrough',
      (payload) => {
        const { method, walkthroughId, initialStepIndex } = payload ?? {};
        invariant(method, 'method is required');

        if (method === 'start' && walkthroughId) {
          void startWalkthrough(walkthroughId, initialStepIndex);
        } else if (method === 'stop') {
          void stopWalkthrough();
        }
      },
    );

    return () => {
      unsubscribePageLoad();
      unsubscribePageUnload();
      unsubscribeFetchedWalkthroughs();
      unsubscribeControlStep();
      unsubscribeGoToStep();
      unsubscribeControlWalkthrough();
    };
  }, [
    handlePageLoad,
    handlePageUnload,
    handleWalkthroughsFetched,
    walkthroughInstanceEventBus,
    moveStep,
    goToStep,
    startWalkthrough,
    stopWalkthrough,
  ]);
}

function WalkthroughManagerEffects({ instanceId }: { instanceId: string }) {
  const walkthroughInstanceEventBus = useMemo(
    () => createWalkthroughInstanceEventBus(instanceId),
    [instanceId],
  );

  useWalkthroughManagerEffects(walkthroughInstanceEventBus);
  return null;
}

export default WalkthroughManagerEffects;
